import React, { useState, useEffect, useRef } from 'react';
import { 
  Toolbar, 
  IconButton, 
  Typography, 
  Box, 
  Badge, 
  Menu, 
  MenuItem, 
  Avatar, 
  Tooltip, 
  ListItemIcon, 
  ListItemText,
  Divider,
  Button,
  FormControl,
  Select,
  useTheme,
  useMediaQuery,
  styled,
  Chip,
  Paper,
  Snackbar,
  Alert,
  alpha,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  AccountCircle,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  CalendarMonth as CalendarIcon,
  Circle as CircleIcon,
  Close as CloseIcon,
  Person as ProfileIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useRTL from '../../hooks/useRTL';
import { useAuth } from '../../context/AuthContext';
import { useUnreadCount, useNotifications, useMarkAsRead } from '../../api/useNotifications';
import { useThemeContext } from '../../context/ThemeContext';
import { useDateFormat } from '../../context/DateFormatContext.jsx';
import { getLocalizedRelativeTime } from '../../utils/dayjsLocalization';
import { playNotificationSound } from '../../utils/notificationSound';
import { toast } from 'react-toastify';

// RTL-friendly MenuItem component
const RTLMenuItem = styled(MenuItem)(({ theme, isRTL }) => ({
  ...(isRTL && {
    flexDirection: 'row-reverse',
    textAlign: 'right',
    '& .MuiListItemIcon-root': {
      marginLeft: 0,
      marginRight: 'auto',
    },
  }),
}));

const Header = () => {
  const { t, i18n } = useTranslation(['app', 'common', 'auth', 'navigation', 'calendar']);
  const { token, user, logout } = useAuth();
  const { isRTL, getRTLProps } = useRTL();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Notifications data - only fetch unread count initially
  const { data: unreadCount, refetch: refetchUnread } = useUnreadCount(token);
  const [unreadBadge, setUnreadBadge] = useState(0);
  const [previousUnreadCount, setPreviousUnreadCount] = useState(0);
  const [showNewNotificationToast, setShowNewNotificationToast] = useState(false);
  const [newNotificationMessage, setNewNotificationMessage] = useState('');
  const [shouldLoadNotifications, setShouldLoadNotifications] = useState(false);
  
  // Manual refresh function for unread count
  const refreshUnreadCount = () => {
    refetchUnread();
  };
  
  // Periodic refresh for unread count (only when app is focused)
  useEffect(() => {
    if (!token) return;
    
    const interval = setInterval(() => {
      // Only refresh if the app is focused
      if (document.hasFocus()) {
        refreshUnreadCount();
      }
    }, 60000); // Refresh every minute when focused
    
    return () => clearInterval(interval);
  }, [token]);
  
  useEffect(() => {
    if (typeof unreadCount === 'number') {
      // Check if there are new notifications
      if (unreadCount > previousUnreadCount && previousUnreadCount > 0) {
        const newNotificationsCount = unreadCount - previousUnreadCount;
        setNewNotificationMessage(t('notifications:new_notification_received', { count: newNotificationsCount }));
        setShowNewNotificationToast(true);
        
        // Play notification sound
        playNotificationSound();
      }
      setUnreadBadge(unreadCount);
      setPreviousUnreadCount(unreadCount);
    }
  }, [unreadCount, previousUnreadCount, t]);
  
  // Only fetch notifications when dropdown is opened
  const { data: notifResp, refetch: refetchNotifs, isLoading: isLoadingNotifications } = useNotifications(token, { 
    limit: 15,
    enabled: shouldLoadNotifications && !!token 
  });
  const notifications = notifResp?.data || [];
  const { mutate: markAsRead, isPending: markingRead } = useMarkAsRead(token);
  const { mode, toggleTheme } = useThemeContext();
  const navigate = useNavigate();
  
  // State for user menu
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const { dateFormat, setDateFormat } = useDateFormat();

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleNotificationOpen = (event) => {
    setNotificationAnchor(event.currentTarget);
    setUnreadBadge(0); // Reset badge count when opening notifications
    setShouldLoadNotifications(true); // Trigger notifications loading
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const handleCloseNewNotificationToast = () => {
    setShowNewNotificationToast(false);
  };

  const handleLanguageChange = (event) => {
    const newLanguage = event.target.value;
    console.log('Changing language to:', newLanguage);
    i18n.changeLanguage(newLanguage);
    console.log('Language changed to:', i18n.language);
  };

  const handleCalendarChange = (event) => {
    setDateFormat(event.target.value);
  };

  const handleLogout = () => {
    logout();
    handleUserMenuClose();
  };

  const handleProfileClick = () => {
    navigate('/profile');
    handleUserMenuClose();
  };

  const handleSettingsClick = () => {
    navigate('/settings');
    handleUserMenuClose();
  };

  const handleRefreshNotifications = () => {
    refetchNotifs();
    refreshUnreadCount();
  };

  // Mobile drawer toggle function
  const handleMobileDrawerToggle = () => {
    const event = new CustomEvent('mobile-drawer-toggle');
    window.dispatchEvent(event);
  };

  return (
    <Toolbar sx={{ 
      minHeight: '64px !important',
      height: 64,
      px: { xs: 1, sm: 2 },
      justifyContent: 'space-between',
      background: 'transparent',
      gap: { xs: 1, sm: 2 },
    }}>
      {/* Left side - Menu button and title */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2,
        flexDirection: isRTL ? 'row-reverse' : 'row'
      }}>
        {/* Mobile Burger Menu Button */}
        {isMobile && (
          <Tooltip title={t('common:menu')}>
            <IconButton
              onClick={handleMobileDrawerToggle}
              sx={{ 
                color: theme.palette.text.primary,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                }
              }}
            >
              <MenuIcon />
            </IconButton>
          </Tooltip>
        )}

        {/* App Title - Hidden on mobile */}
        {!isMobile && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: isRTL ? 'flex-end' : 'flex-start',
            textAlign: isRTL ? 'right' : 'left'
          }}>
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 700,
                fontSize: '1.1rem',
                color: theme.palette.text.primary,
                lineHeight: 1.2,
                letterSpacing: '0.01em',
              }}
            >
              {t('app:title', 'Document Tracking System')}
            </Typography>
            <Typography
              variant="caption"
              component="div"
              sx={{
                fontWeight: 500,
                fontSize: '0.75rem',
                color: theme.palette.text.secondary,
                letterSpacing: '0.01em',
              }}
            >
              {t('app:subtitle', 'د اسنادو دتعقیب سیستم')}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Right side - Controls (Reorganized: Profile, Dark Mode, Notifications, Language, Calendar) */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: { xs: 0.5, sm: 1 },
        flexDirection: isRTL ? 'row-reverse' : 'row',
        flexWrap: 'nowrap',
        overflow: 'hidden'
      }}>
        {/* User Profile - First */}
        <Tooltip title={t('common:profile')}>
          <IconButton
            onClick={handleUserMenuOpen}
            size={isMobile ? "small" : "medium"}
            sx={{ 
              color: theme.palette.text.primary,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              }
            }}
          >
            <Avatar sx={{ 
              width: isMobile ? 28 : 32, 
              height: isMobile ? 28 : 32, 
              fontSize: isMobile ? '0.8rem' : '0.9rem' 
            }}>
              {user?.username?.charAt(0)?.toUpperCase() || 'U'}
            </Avatar>
          </IconButton>
        </Tooltip>

        {/* Theme Toggle - Second */}
        <Tooltip title={mode === 'dark' ? t('common:lightMode') : t('common:darkMode')}>
          <IconButton
            onClick={toggleTheme}
            size={isMobile ? "small" : "medium"}
            sx={{ 
              color: theme.palette.text.primary,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              }
            }}
          >
            {mode === 'dark' ? <LightModeIcon fontSize={isMobile ? "small" : "medium"} /> : <DarkModeIcon fontSize={isMobile ? "small" : "medium"} />}
          </IconButton>
        </Tooltip>

        {/* Notifications - Third */}
        <Tooltip title={t('notifications:title')}>
          <IconButton
            onClick={handleNotificationOpen}
            size={isMobile ? "small" : "medium"}
            sx={{ 
              color: theme.palette.text.primary,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              }
            }}
          >
            <Badge badgeContent={unreadBadge} color="error" sx={{
              '& .MuiBadge-badge': {
                fontSize: isMobile ? '0.6rem' : '0.75rem',
                height: isMobile ? 16 : 18,
                minWidth: isMobile ? 16 : 18,
              }
            }}>
              <NotificationsIcon fontSize={isMobile ? "small" : "medium"} />
            </Badge>
          </IconButton>
        </Tooltip>

        {/* Language Selector - Fourth */}
        <FormControl size="small" sx={{ minWidth: isMobile ? 80 : 100 }}>
          <Select
            value={i18n.language}
            onChange={handleLanguageChange}
            displayEmpty
            sx={{
              '& .MuiSelect-select': {
                py: 1,
                px: 1.5,
                fontSize: isMobile ? '0.7rem' : '0.8rem',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.divider,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
          >
            <MenuItem value="dari">دری</MenuItem>
            <MenuItem value="ps">پښتو</MenuItem>
          </Select>
        </FormControl>

        {/* Calendar Format - Always visible */}
        <FormControl size="small" sx={{ minWidth: isMobile ? 80 : 120 }}>
          <Select
            value={dateFormat}
            onChange={handleCalendarChange}
            displayEmpty
            sx={{
              '& .MuiSelect-select': {
                py: 1,
                px: 1.5,
                fontSize: isMobile ? '0.7rem' : '0.8rem',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.divider,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
          >
            <MenuItem value="qamari">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon sx={{ fontSize: isMobile ? 14 : 16 }} />
                {t('calendar:qamari')}
              </Box>
            </MenuItem>
            <MenuItem value="shamsi">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon sx={{ fontSize: isMobile ? 14 : 16 }} />
                {t('calendar:shamsi')}
              </Box>
            </MenuItem>
            <MenuItem value="gregorian">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon sx={{ fontSize: isMobile ? 14 : 16 }} />
                {t('calendar:gregorian')}
              </Box>
            </MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* User Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        PaperProps={{
          elevation: 8,
          sx: {
            minWidth: 200,
            borderRadius: 2,
            mt: 1,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: `1px solid ${theme.palette.divider}`,
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {user?.username || user?.email || t('common:user')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.role?.name || t('common:user')}
          </Typography>
        </Box>
        
        <RTLMenuItem onClick={handleProfileClick} isRTL={isRTL}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('common:profile')} />
        </RTLMenuItem>
        
        <RTLMenuItem onClick={handleSettingsClick} isRTL={isRTL}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('app:settings')} />
        </RTLMenuItem>
        
        <Divider />
        
        <RTLMenuItem onClick={handleLogout} isRTL={isRTL}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('auth:logout')} />
        </RTLMenuItem>
      </Menu>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationAnchor}
        open={Boolean(notificationAnchor)}
        onClose={handleNotificationClose}
        PaperProps={{
          elevation: 8,
          sx: {
            minWidth: 320,
            maxWidth: 400,
            maxHeight: 580,
            borderRadius: 1,
            mt: 1.5,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: `2px solid ${alpha(theme.palette.divider, 0.1)}`,
            overflow: 'hidden'
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Header */}
        <Box sx={{ 
          p: 2, 
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'primary.dark',
          flexShrink: 0,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <NotificationsIcon sx={{ fontSize: 18, color: 'white' }} />
            </Box>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem' }}>
              {t('app:notifications')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={unreadBadge} 
              size="small" 
              sx={{ 
                backgroundColor: 'error.main',
                color: 'white',
                fontWeight: 700,
                fontSize: '0.75rem',
                height: 24,
                minWidth: 24,
                '& .MuiChip-label': {
                  px: 1
                }
              }}
            />
            <IconButton
              size="small"
              onClick={handleRefreshNotifications}
              disabled={isLoadingNotifications}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)'
                },
                '&:disabled': {
                  color: 'rgba(255,255,255,0.5)'
                }
              }}
            >
              <RefreshIcon sx={{ 
                fontSize: 16,
                animation: isLoadingNotifications ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }} />
            </IconButton>
          </Box>
        </Box>

        {/* Enhanced Notifications List */}
        <Box sx={{ 
          maxHeight: 380,
          overflowY: 'auto',
          overflowX: 'hidden',
          backgroundColor: 'background.paper'
        }}>
          {notifications.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Box sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: 'grey.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2
              }}>
                <NotificationsIcon sx={{ fontSize: 32, color: 'text.disabled' }} />
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: isRTL ? 'right' : 'left' }}
              >
                {t('notifications:no_notifications', 'No notifications found')}
              </Typography>
            </Box>
          ) : (
            notifications.map((n) => {
              console.log('Header rendering notification:', n);
              
              // Get translated title
              const getTranslatedTitle = (title) => {
                if (!title) return '';
                
                const titleKey = title.toLowerCase().replace(/\s+/g, '_');
                const translationKey = `notifications:titles.${titleKey}`;
                const translatedTitle = t(translationKey);
                
                // If translation doesn't exist, return original title
                return translatedTitle !== translationKey ? translatedTitle : title;
              };
              
              // Format notification message based on action type
              const getFormattedMessage = () => {
                const username = n.fromUser?.full_name || n.fromUser?.username || 'Unknown User';
                const docTitle = n.document?.title || 'Untitled Document';
                const docRef = n.document?.reference_number || docTitle;
                const docType = n.document?.type || '';
                const assignedUsername = n.toUser?.full_name || n.toUser?.username || '';
                
                if (!n || !n.title) return n.message;
                const lower = n.title.toLowerCase();
                
                // Check if message already contains assigner name (for bulk assignments)
                const messageContainsAssigner = n.message && n.message.includes('assigned you a task');
                console.log('Header - Message:', n.message);
                console.log('Header - Contains assigner:', messageContainsAssigner);
                console.log('Header - Title:', n.title);
                console.log('Header - Lower title:', lower);
                
                switch (lower) {
                  case 'task assigned':
                  case 'new task assigned':
                    // If message already contains assigner name (bulk assignment), localize it
                    if (messageContainsAssigner) {
                      // Extract document reference from the message
                      const docMatch = n.message.match(/document\s+([^\s]+)/);
                      const documentRef = docMatch ? docMatch[1].trim() : docRef;
                      console.log('Header - Document ref:', documentRef);
                      console.log('Header - Username:', username);
                      return t('notifications:task_assigned_bulk_message', { 
                        user: username, 
                        document: documentRef 
                      });
                    }
                    return `${username}, ${docTitle}, ${docType} ${t('notifications:task_assigned', 'assigned')}`;
                  case 'task in progress':
                    return `${username}, ${docTitle}, ${docType} ${t('notifications:task_in_progress', 'in progress')}`;
                  case 'task forwarded':
                    return `${username}, ${docTitle}, ${docType} ${t('notifications:task_forwarded', 'forwarded')}, ${assignedUsername}`;
                  case 'task completed':
                    return `${username}, ${docTitle}, ${docType} ${t('notifications:task_completed', 'completed')}`;
                  case 'task rejected':
                    return `${username}, ${docTitle}, ${docType} ${t('notifications:task_rejected', 'rejected')}`;
                  case 'task accepted':
                    return `${username}, ${docTitle}, ${docType} ${t('notifications:task_accepted', 'accepted')}`;
                  default:
                    return n.message;
                }
              };
              
              const message = getFormattedMessage();
              const relativeTime = getLocalizedRelativeTime(n.createdAt || n.created_at, i18n.language);
              const translatedTitle = getTranslatedTitle(n.title);
              console.log('Header notification message:', message);
              console.log('Header translated title:', translatedTitle);

              return (
                <Paper
                  key={n.id}
                  elevation={0}
                  sx={{
                    m: 1,
                    p: 1.5,
                    backgroundColor: n.isRead ? 'background.paper' : 'primary.50',
                    border: '1px solid',
                    borderColor: n.isRead ? 'divider' : 'primary.main',
                    borderRadius: 1,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    position: 'relative',
                    '&:hover': {
                      backgroundColor: n.isRead ? 'action.hover' : 'primary.100',
                      transform: 'translateY(-1px)',
                      boxShadow: 2
                    }
                  }}
                  onClick={() => {
                    console.log('Notification clicked:', n.id, 'isRead:', n.isRead);
                    
                    // Mark notification as read if it's unread
                    if (!n.isRead) {
                      console.log('Marking notification as read:', n.id);
                      markAsRead(n.id, {
                        onSuccess: () => {
                          console.log('Notification marked as read successfully:', n.id);
                          // Refresh notifications to update the UI
                          refetchNotifs();
                          refreshUnreadCount();
                        },
                        onError: (error) => {
                          console.error('Error marking notification as read:', error);
                          console.error('Error response:', error.response?.data);
                        }
                      });
                    } else {
                      console.log('Notification already read:', n.id);
                    }
                    
                    // Navigate to the link if available
                    if (n.link) {
                      console.log('Navigating to:', n.link);
                      navigate(n.link);
                    }
                    handleNotificationClose();
                  }}
                >
                  {/* Unread indicator */}
                  {!n.isRead && (
                    <Box sx={{
                      position: 'absolute', 
                      top: 6,
                      right: isRTL ? 'auto' : 6,
                      left: isRTL ? 6 : 'auto',
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      zIndex: 1
                    }} />
                  )}
                  
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: 1,
                    flexDirection: isRTL ? 'row-reverse' : 'row',
                    opacity: markingRead ? 0.7 : 1,
                    pointerEvents: markingRead ? 'none' : 'auto'
                  }}>
                    {/* Avatar */}
                    <Avatar 
                      src={n.fromUser?.profileImage} 
                      sx={{ 
                        width: 36, 
                        height: 36,
                        flexShrink: 0,
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        backgroundColor: n.isRead ? 'grey.400' : 'primary.main',
                        color: n.isRead ? 'grey.700' : 'white'
                      }}
                    >
                      {n.fromUser?.full_name?.charAt(0) || n.fromUser?.username?.charAt(0) || 'U'}
                    </Avatar>
                    
                    {/* Content */}
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      {/* Header with name */}
                      <Typography 
                        variant="subtitle2" 
                        fontWeight={n.isRead ? 600 : 700}
                        color={n.isRead ? 'text.primary' : 'primary.main'}
                        sx={{ 
                          textAlign: isRTL ? 'right' : 'left',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          mb: 0.5,
                          fontSize: '0.8rem',
                          lineHeight: 1.2
                        }}
                      >
                        {n.fromUser?.full_name || n.fromUser?.username || 'Unknown User'}
                      </Typography>
                      
                      {/* Message */}
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          textAlign: isRTL ? 'right' : 'left',
                          mb: 0.5,
                          lineHeight: 1.3,
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          fontWeight: n.isRead ? 400 : 500,
                          fontSize: '0.75rem'
                        }}
                      >
                        {message}
                      </Typography>
                      
                      {/* Time */}
                      <Typography 
                        variant="caption" 
                        color="text.disabled"
                        sx={{ 
                          textAlign: isRTL ? 'right' : 'left',
                          fontSize: '0.65rem',
                          fontWeight: 500,
                          display: 'block',
                          lineHeight: 1.2
                        }}
                      >
                        {relativeTime}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              );
            })
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ 
          p: 0.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'grey.50',
          flexShrink: 0
        }}>
          <Button
            fullWidth
            variant="text"
            onClick={() => {
              navigate('/notifications');
              handleNotificationClose();
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              color: 'primary.main',
              py: 1,
              fontSize: '0.875rem',
              '&:hover': {
                backgroundColor: 'primary.50'
              }
            }}
          >
            {t('common:view_all', 'View All Notifications')}
          </Button>
        </Box>
      </Menu>

      {/* New Notification Toast */}
      <Snackbar
        open={showNewNotificationToast}
        autoHideDuration={4000}
        onClose={handleCloseNewNotificationToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        sx={{ zIndex: 9999 }}
      >
        <Alert
          onClose={handleCloseNewNotificationToast}
          severity="info"
          icon={<NotificationsIcon />}
          sx={{
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            '& .MuiAlert-icon': {
              color: theme.palette.primary.main,
            },
          }}
        >
          {newNotificationMessage}
        </Alert>
      </Snackbar>
    </Toolbar>
  );
};

export default Header;